import AdvancedController from "./advanced-controller";
import { Breakpoints } from "../utils/breakpoints";
import { debounce } from "../utils/utils";
import Hunt from "huntjs";
import Swiper, { Navigation, Pagination, Autoplay } from "swiper";

const AUTO_HEIGHT_BREAKPOINT = "sm";

export default class SwiperController extends AdvancedController {
  static targets = ["swiper"];

  static values = {
    autoplay: Boolean,
    sameheight: Boolean,
    onlyautoheightmobile: Boolean,
    maxslides: Boolean
  };

  _slidesPerView;
  _autoplay;
  _autoplayDelay;
  _loop;
  _totalSlides;
  _slidesCloned;
  _spaceBetween;
  _swiper;
  _disableGrab;

  connect() {
    super.connect();
    this._slidesPerView = this.swiperTarget.getAttribute("data-slides-per-view") || 1;
    this._autoplay = !!+this.swiperTarget.getAttribute("data-autoplay-enable");
    this._autoplayDelay = this.swiperTarget.getAttribute("data-autoplay-delay");
    this._loop = !!+this.swiperTarget.getAttribute("data-loop-enable");
    this._spaceBetween = this.swiperTarget.getAttribute("data-space-between");
    this._totalSlides = this.swiperTarget.querySelectorAll(".swiper-slide");
    this._slidesCloned = false;
    this._disableGrab = this.swiperTarget.getAttribute("data-disable-grab") || false;
    this.createSlider(this.swiperTarget, !this.sameheightValue);
    this.breakpoints = new Breakpoints();

    //disable autoslide on start, as it gets handled by huntjs if in viewport
    if (this.hasSwiperTarget && this.onlyautoheightmobileValue) {
      this.manageAutoHeight();
      window.addEventListener("resize", () =>
        debounce(this.manageAutoHeight(), 250)
      );
    }

    this.disableAutoswipe();
    new Hunt(this.element, {
      persist: true,
      enter: () => this.enableAutoswipe(),
      leave: () => this.disableAutoswipe(),
    });

    window.addEventListener('beforeprint', this.destroySwiper.bind(this));
  }

  destroySwiper() {
    if (this.hasSwiperTarget && this.swiperTarget.swiper) {
      this.swiperTarget.swiper.destroy();
      this._swiper = null;
      this._slidesCloned = false;
    }
  }

  enableAutoswipe() {
    // class instead of prop needs to be checked because autoplay prop is also on non-autoplay instances
    if (
      this.hasSwiperTarget &&
      this.autoplayValue &&
      !this.swiperTarget.swiper.autoplay.running
    ) {
      this.swiperTarget.swiper.autoplay.start();
    }
  }

  disableAutoswipe() {
    if (
      this.hasSwiperTarget &&
      this.autoplayValue &&
      this.swiperTarget.swiper &&
      this.swiperTarget.swiper.autoplay.running
    ) {
      this.swiperTarget.swiper.autoplay.stop();
    }
  }

  manageAutoHeight() {
    if (
      this.hasSwiperTarget &&
      this.breakpoints.breakpointDown(AUTO_HEIGHT_BREAKPOINT) &&
      !this.swiperTarget.swiper.params.autoHeight
    ) {
      this.destroyAndReinit(true);
    }
    if (
      this.hasSwiperTarget &&
      this.breakpoints.breakpointUp(AUTO_HEIGHT_BREAKPOINT) &&
      this.swiperTarget.swiper.params.autoHeight
    ) {
      this.destroyAndReinit(false);
    }
  }

  destroyAndReinit(autoHeight) {
    this.swiperTarget.swiper.destroy();
    this.createSlider(this.swiperTarget, autoHeight);
  }

  createSlider(element, autoHeight = false) {
    const config = {
      modules: [Navigation, Pagination, Autoplay],
      slidesPerView: this._slidesPerView,
      grabCursor: !this._disableGrab,
      allowTouchMove: !this._disableGrab,
      navigation: {
        prevEl: element.querySelector(".swiper-button-prev"),
        nextEl: element.querySelector(".swiper-button-next"),
      },
      pagination: {
        el: element.querySelector('.swiper-pagination'),
        bulletActiveClass: 'current',
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '">' + (index + 1) + "</span>";
        },
      },
    };

    if (this._autoplay) {
      config.autoplay = {
        delay: Number(this._autoplayDelay),
        pauseOnMouseEnter: true,
        disableOnInteraction: false,
      };
    }

    if (this._spaceBetween === "") {
      config.spaceBetween = 24; // --ts-gutter-x
    }

    if (this._slidesPerView > 1) {
      const swiperBreakpoints = this.calculateSwiperBreakpoints(this._slidesPerView);
      config.breakpoints = swiperBreakpoints;
      config.centeredSlides = false;
    }

    if (this._loop) {
      config.loop = true;
    }

    // turn off pagination bc cloned slides would show up as well
    if (this._slidesCloned) {
      config.pagination = false;
    }

    if (autoHeight) {
      config.autoHeight = true;
    }

    this._swiper = new Swiper(element, config);
  }

  calculateSwiperBreakpoints(slidesPerView) {
    // this will be returned
    const swiperBreakpoints = {};
    const breakpointsService = new Breakpoints();
    const allBreakpoints = breakpointsService.getAll();
    // add xxl breakpoint
    allBreakpoints.xxl = 1600;

    // calculate the step size between each breakpoint, e.g. 8 / 4 = 2 (=> xs: 1, sm: 1+2, md: 1+2+2)
    const perBreakpoint =
      slidesPerView / (Object.keys(allBreakpoints).length + 1);

    // sort the breakpoints by pixel values for the loop
    const breakpointPixelValues = Object.values(allBreakpoints).sort(
      (a, b) => a - b
    );

    let i = 1;
    // force add a breakpoint for 0px
    swiperBreakpoints[0] = { slidesPerView: 1 };
    for (const breakpointSize of breakpointPixelValues) {
      i++;
      // calculate how much slides shall be displayed
      const slidesPerBreakpoint = Math.floor(1 + i * perBreakpoint);

      // add the config
      swiperBreakpoints[breakpointSize] = {
        slidesPerView: this.getSlidesPerBreakpoint(slidesPerBreakpoint, slidesPerView),
      };
    }

    return swiperBreakpoints;
  }

  getSlidesPerBreakpoint(slidesPerBreakpoint, slidesPerView) {
    if (this.maxslidesValue && slidesPerBreakpoint > slidesPerView) {
      slidesPerBreakpoint = slidesPerView;
    }

    // check if there are more slides than slidesPerView * 2
    // otherwise loop doesn't work
    if (this._loop && !this._slidesCloned && this._totalSlides.length <= slidesPerBreakpoint * 2) {
      this.handleLoop();
    }

    return slidesPerBreakpoint;
  }

  handleLoop() {
    this._totalSlides.forEach((slide) => {
      this.swiperTarget
        .querySelector(".swiper-wrapper")
        .appendChild(slide.cloneNode(true));
    });
    this._slidesCloned = true;
  }
}
